/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, inject } from '@angular/core';
import { PdfStudioService } from './pdf-studio.service';

/** One page drawn as a JPEG, with its size in PDF points. */
export interface PageShot {
  jpeg: Uint8Array;
  width: number;
  height: number;
}

/** Draws one page (0-based) of a document as a picture. */
export type PageRenderer = (pageIndex: number) => Promise<PageShot>;

/** The longest side of a redacted page, in pixels: sharp enough to read, small enough to keep the file reasonable. */
const TARGET_LONG_SIDE = 2200;

/**
 * Makes a redaction real.
 *
 * Painting a black box over text leaves the text in the file: anyone can select
 * it, copy it, or read it out of the page's content stream. For a mark that is
 * meant to protect personal data that is worse than useless, because it looks
 * finished. So a page that carries a redaction is not edited, it is **replaced**:
 * drawn with its boxes on, and swapped for a picture of that. The words under the
 * boxes are no longer in the file at all.
 *
 * The result is a new document built from only what it needs — the other pages
 * are copied over, the redacted ones are a picture — and not the old document
 * with a page taken out, because pdf-lib writes every object it was ever given:
 * a page removed from the tree would still be in the bytes.
 */
@Injectable({ providedIn: 'root' })
export class PdfRedactionService {
  private readonly studio = inject(PdfStudioService);

  /**
   * `burned` is the document with its black boxes already drawn. Pages named in
   * `pages` (0-based) are replaced by a picture of themselves; the rest are
   * carried over as they are.
   */
  async flatten(burned: Uint8Array, pages: Iterable<number>, render?: PageRenderer): Promise<Uint8Array> {
    const flagged = new Set(pages);
    const lib = await this.studio.lib();
    const source = await lib.PDFDocument.load(burned.slice(), { ignoreEncryption: true, updateMetadata: false });
    const drawing = render ? undefined : await this.pdfjsRenderer(burned);
    // with nothing to draw a page with, a flagged page must fail — never be copied over unredacted
    const draw: PageRenderer = render ?? drawing?.render ?? (() => Promise.reject(new Error('No way to draw the page.')));

    try {
      const out = await lib.PDFDocument.create();
      for (let index = 0; index < source.getPageCount(); index++) {
        if (flagged.has(index)) {
          const shot = await draw(index);
          const image = await out.embedJpg(shot.jpeg);
          out.addPage([shot.width, shot.height]).drawImage(image, { x: 0, y: 0, width: shot.width, height: shot.height });
        } else {
          const [copy] = await out.copyPages(source, [index]);
          out.addPage(copy);
        }
      }

      const title = source.getTitle();
      if (title) {
        out.setTitle(title);
      }
      return await out.save();
    } finally {
      await drawing?.destroy();
    }
  }

  /** Draws pages with pdf.js — the same engine that shows them in the studio. */
  private async pdfjsRenderer(bytes: Uint8Array): Promise<{ render: PageRenderer; destroy: () => Promise<void> }> {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;
    const pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;

    const render: PageRenderer = async (index) => {
      const page = await pdf.getPage(index + 1);
      try {
        const natural = page.getViewport({ scale: 1 });
        const scale = Math.min(3, Math.max(1.5, TARGET_LONG_SIDE / Math.max(natural.width, natural.height)));
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('This browser cannot draw the page.');
        }
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: context, viewport }).promise;

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        canvas.width = 0;
        canvas.height = 0;
        if (!blob) {
          throw new Error('The page could not be turned into a picture.');
        }
        return { jpeg: new Uint8Array(await blob.arrayBuffer()), width: natural.width, height: natural.height };
      } finally {
        page.cleanup();
      }
    };

    return { render, destroy: () => pdf.destroy() };
  }
}
